// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { FileCardItem } from '@/components/common/FileCard';

// ** Components
import SwiperCarousel from '@/components/common/carousel/SwiperCarousel';
import ComponentElementsFileCard from '@/components/strapi/Elements/ComponentElementsFileCard';
import { Icon } from '@/components/common/icons';

// ** Utils
import { downloadByUrl } from '@/components/common/FileCard';

type ExtendedFileCardItem = FileCardItem & {
	documentId: string;
	rawFile: any;
	fileName: string;
	isAuthenticated?: boolean;
	accessRestriction?: string | null;
};

export type FileCarouselProps = {
	items: ExtendedFileCardItem[];
	currentUser: any;
	fileBlockAuthenticated?: boolean;
	selectAllLabel?: string;
	onSelectAll?: (items: ExtendedFileCardItem[]) => void;
	onDownloadSelected?: (items: ExtendedFileCardItem[]) => void;
	sx?: SxProps<Theme>;
};

const deselectAllLabel = 'Alle abwählen';

const FileCarousel = ({
	items,
	currentUser,
	fileBlockAuthenticated = false,
	selectAllLabel = 'Alle auswählen',
	onSelectAll,
	onDownloadSelected
}: FileCarouselProps) => {
	const theme = useTheme();
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
	const [isDownloading, setIsDownloading] = React.useState(false);

	// ── STRICT AUTH MATRIX CHECK ──
	const isLoggedIn = React.useMemo(() => {
		return Boolean(currentUser && (currentUser.email || currentUser.id || currentUser.user));
	}, [currentUser]);

	// ── SCALABLE PERMISSION FILTER ENGINE ──
	// Determines exactly which items this user is allowed to select/download
	const downloadableItems = React.useMemo(() => {
		return items.filter((item) => {
			// Priority 1: Parent Global Override Lockout
			if (fileBlockAuthenticated && !isLoggedIn) return false;

			// Priority 2: Custom Group/Role Layer
			const accessRestriction = item.accessRestriction;
			if (accessRestriction && (!isLoggedIn || currentUser?.role !== accessRestriction)) {
				return false;
			}

			// Priority 3: Individual File Level Settings Fallback
			if (item.isAuthenticated && !isLoggedIn) return false;

			return true;
		});
	}, [items, fileBlockAuthenticated, isLoggedIn, currentUser?.role]);

	// Wipe selected cache if items structurally modify or permissions break dynamically
	React.useEffect(() => {
		const allowed = new Set(downloadableItems.map((i) => i.documentId));
		setSelectedIds((prev) => {
			if (prev.size === 0) return prev;
			const next = new Set<string>();
			for (const id of prev) {
				if (allowed.has(id)) next.add(id);
			}
			return next.size === prev.size ? prev : next;
		});
	}, [downloadableItems]);

	const selectedItems = React.useMemo(
		() => downloadableItems.filter((it) => selectedIds.has(it.documentId)),
		[downloadableItems, selectedIds]
	);

	const selectedCount = selectedItems.length;

	// Evaluation maps specifically against valid downloadable objects
	const isAllSelected = downloadableItems.length > 0 && selectedIds.size === downloadableItems.length;
	const isActionsDisabled = downloadableItems.length === 0;

	const reservedCtaLabel = selectAllLabel.length >= deselectAllLabel.length ? selectAllLabel : deselectAllLabel;

	const handleToggleItem = React.useCallback(
		(nextSelected: boolean, documentId: string) => {
			// Prevent unallowed files from ever manipulating state keys
			const isValid = downloadableItems.some((i) => i.documentId === documentId);
			if (!isValid) return;

			setSelectedIds((prev) => {
				const next = new Set(prev);
				if (nextSelected) next.add(documentId);
				else next.delete(documentId);
				return next;
			});
		},
		[downloadableItems]
	);

	const handleSelectAll = React.useCallback(() => {
		if (isActionsDisabled) return;

		setSelectedIds((prev) => {
			const validIds = downloadableItems.map((i) => i.documentId);
			const isAll = validIds.length > 0 && prev.size === validIds.length;
			const next = isAll ? new Set<string>() : new Set(validIds);

			setTimeout(() => onSelectAll?.(items.filter((i) => next.has(i.documentId))), 0);
			return next;
		});
	}, [downloadableItems, onSelectAll, items, isActionsDisabled]);

	const handleDownloadSelected = React.useCallback(async () => {
		if (isActionsDisabled || selectedItems.length === 0) return;

		if (onDownloadSelected) {
			onDownloadSelected(selectedItems);
			return;
		}

		if (selectedItems.length === 1) {
			downloadByUrl(selectedItems[0].url, selectedItems[0].fileName);
			setSelectedIds(new Set());
			return;
		}

		setIsDownloading(true);
		try {
			const [JSZipModule, FileSaverModule] = await Promise.all([import('jszip'), import('file-saver')]);
			const JSZip = JSZipModule.default || JSZipModule;
			const { saveAs } = FileSaverModule;
			const zip = new JSZip();

			const results = await Promise.all(
				selectedItems.map(async (item) => {
					try {
						const res = await fetch(item.url);
						if (!res.ok) throw new Error(`HTTP ${res.status}`);
						const blob = await res.blob();
						return { blob, item, failed: false };
					} catch {
						return { blob: null, item, failed: true };
					}
				})
			);

			const usedNames = new Map<string, number>();

			results.forEach(({ blob, item, failed }) => {
				if (!failed && blob) {
					let finalName = item.fileName;

					// 1. Ensure extension exists using Regex (avoids false positives on dots in names)
					const hasValidExtension = /\.[a-z0-9]{2,4}$/i.test(finalName);

					if (!hasValidExtension) {
						const urlExtension = item.url.split(/[#?]/)[0].split('.').pop();
						const mimeExtension = blob.type.split('/')[1];

						const extension =
							urlExtension && urlExtension.length < 5
								? urlExtension
								: mimeExtension === 'octet-stream'
									? 'bin'
									: mimeExtension || 'bin';

						finalName = `${finalName}.${extension.replace(/[^a-z0-9]/gi, '')}`;
					}

					// 2. Handle duplicates (using lowercase for case-insensitive tracking)
					const normalizedKey = finalName.toLowerCase();
					if (usedNames.has(normalizedKey)) {
						const count = (usedNames.get(normalizedKey) ?? 0) + 1;
						usedNames.set(normalizedKey, count);

						const dotIndex = finalName.lastIndexOf('.');
						if (dotIndex !== -1) {
							finalName = `${finalName.substring(0, dotIndex)} (${count})${finalName.substring(dotIndex)}`;
						} else {
							finalName = `${finalName} (${count})`;
						}
					} else {
						usedNames.set(normalizedKey, 0);
					}

					zip.file(finalName, blob);
				}
			});

			const content = await zip.generateAsync({ type: 'blob' });
			saveAs(content, 'downloads.zip');
			setSelectedIds(new Set());
		} catch (error) {
			console.error('ZIP generation failed:', error);
		} finally {
			setIsDownloading(false);
		}
	}, [selectedItems, isActionsDisabled, onDownloadSelected]);

	const arrowButtonSx = React.useMemo(
		() => ({
			p: 0.5,
			borderRadius: 999,
			color: theme.palette.common.black,
			backgroundColor: 'transparent !important',
			transition: 'transform 140ms ease, background-color 140ms ease, box-shadow 140ms ease',
			'&:hover': {
				transform: 'translateY(-1px)',
				color: theme.palette.primary.main,
				backgroundColor: 'transparent !important',
				boxShadow: 'none'
			},
			'&:focusVisible': { backgroundColor: 'transparent !important' },
			'&.Mui-focusVisible': { backgroundColor: 'transparent !important' },
			'& .MuiTouchRipple-root': { display: 'none' },
			'&:active': { transform: 'translateY(0) scale(0.98)' },
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none',
				'&:hover': { transform: 'none' },
				'&:active': { transform: 'none' }
			}
		}),
		[theme]
	);

	const slides = React.useMemo(
		() =>
			items.map((item) => {
				const isCardAllowed = downloadableItems.some((i) => i.documentId === item.documentId);

				return {
					key: item.documentId,
					content: (
						<ComponentElementsFileCard
							key={item.documentId}
							id={item.documentId}
							__typename="ComponentElementsFileCard"
							dataFile={null}
							fileCardIdentifier={item.documentId}
							file={item.rawFile}
							fileName={item.fileName}
							selected={selectedIds.has(item.documentId)}
							onSelectedChange={(nextSelected) => handleToggleItem(nextSelected, item.documentId)}
							currentUser={currentUser}
							fileBlockAuthenticated={fileBlockAuthenticated}
							isAuthenticated={item.isAuthenticated ?? false}
							accessRestriction={item.accessRestriction ?? null}
							isLocked={!isCardAllowed}
						/>
					)
				};
			}),
		[items, downloadableItems, selectedIds, handleToggleItem, currentUser, fileBlockAuthenticated]
	);

	return (
		<SwiperCarousel
			slides={slides}
			spaceBetween={5}
			viewportSx={{ mt: { xs: 8, md: 10 } }}
			renderHeader={({ canGoPrev, canGoNext, goPrev, goNext }) => (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4
					}}
				>
					<Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
						<ButtonBase
							onClick={handleSelectAll}
							disabled={isActionsDisabled}
							disableRipple
							sx={{
								borderRadius: 1,
								px: 0,
								py: 0,
								color: !isActionsDisabled ? theme.palette.text.primary : theme.palette.text.disabled,
								transition: 'color 140ms ease',
								'&:hover': {
									color: !isActionsDisabled ? theme.palette.primary.main : undefined
								},
								'& .MuiTouchRipple-root': { display: 'none' },
								opacity: !isActionsDisabled ? 1 : 0.5,
								cursor: !isActionsDisabled ? 'pointer' : 'not-allowed'
							}}
						>
							<Box sx={{ position: 'relative', display: 'inline-block' }}>
								<Typography component="span" variant="body-md" sx={{ visibility: 'hidden', whiteSpace: 'nowrap' }}>
									{reservedCtaLabel}
								</Typography>
								<Typography
									component="span"
									variant="body-md"
									sx={{
										position: 'absolute',
										left: 0,
										top: 0,
										color: 'inherit',
										whiteSpace: 'nowrap'
									}}
								>
									{isAllSelected ? deselectAllLabel : selectAllLabel}
								</Typography>
							</Box>
						</ButtonBase>

						<Badge
							overlap="circular"
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							invisible={selectedCount === 0 || isActionsDisabled}
							badgeContent={selectedCount}
							sx={{
								'& .MuiBadge-badge': {
									minWidth: 18,
									height: 18,
									top: '-2px',
									right: '-1px',
									borderRadius: 999,
									backgroundColor: theme.palette.primary.main,
									color: theme.palette.common.white,
									fontSize: 12,
									fontWeight: 600,
									boxShadow: `0 6px 18px ${theme.palette.common.black}24`
								}
							}}
						>
							<IconButton
								size="small"
								onClick={handleDownloadSelected}
								disabled={selectedCount === 0 || isDownloading || isActionsDisabled}
								aria-label="Ausgewählte Dateien herunterladen"
								sx={{
									color: theme.palette.text.secondary,
									p: 0.5,
									borderRadius: 999,
									transition:
										'transform 140ms ease, background-color 140ms ease, box-shadow 140ms ease, color 140ms ease, opacity 140ms ease',
									'@keyframes downloadWiggle': {
										'0%': { transform: 'translateY(0) scale(1) rotate(0deg)' },
										'35%': {
											transform: 'translateY(-2px) scale(1.06) rotate(-8deg)'
										},
										'70%': {
											transform: 'translateY(0) scale(1.02) rotate(6deg)'
										},
										'100%': { transform: 'translateY(0) scale(1) rotate(0deg)' }
									},
									'&:hover': {
										transform: !isActionsDisabled && selectedCount > 0 ? 'translateY(-1px) scale(1.03)' : 'none',
										backgroundColor:
											!isActionsDisabled && selectedCount > 0 ? theme.palette.common.white : 'transparent',
										boxShadow:
											!isActionsDisabled && selectedCount > 0 ? `0 10px 30px ${theme.palette.common.black}14` : 'none'
									},
									'&:hover svg': {
										animation: !isActionsDisabled && selectedCount > 0 ? 'downloadWiggle 520ms ease-in-out' : 'none'
									},
									'@media (prefers-reduced-motion: reduce)': {
										transition: 'none',
										'&:hover': { transform: 'none' },
										'&:hover svg': { animation: 'none' },
										'&:active': { transform: 'none' }
									},
									'&:active': {
										transform: !isActionsDisabled && selectedCount > 0 ? 'translateY(0) scale(0.99)' : 'none'
									}
								}}
							>
								{isDownloading ? <CircularProgress size={20} color="inherit" /> : <Icon name="download" />}
							</IconButton>
						</Badge>
					</Stack>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							columnGap: { xs: 5, lg: 4 },
							color: theme.palette.common.black
						}}
					>
						<IconButton
							size="small"
							onClick={goPrev}
							disabled={!canGoPrev}
							aria-label="Vorherige Dateien"
							disableRipple
							sx={arrowButtonSx}
						>
							<Icon name="arrowLeft" opacity={canGoPrev ? 1 : 0.32} />
						</IconButton>
						<IconButton
							size="small"
							onClick={goNext}
							disabled={!canGoNext}
							aria-label="Nächste Dateien"
							disableRipple
							sx={arrowButtonSx}
						>
							<Icon name="arrowRight" opacity={canGoNext ? 1 : 0.32} />
						</IconButton>
					</Box>
				</Box>
			)}
		/>
	);
};

export default FileCarousel;
